export type CategoryGroupKey =
  | 'general'
  | 'science'
  | 'math'
  | 'tech'
  | 'history'
  | 'economics'
  | 'humanities'
  | 'geography'
  | 'arts'
  | 'sports'
  | 'food'
  | 'entertainment'
  | 'mythology'
  | 'medicine'
  | 'space'
  | 'books'
  | 'music'
  | 'languages'
  | 'wildlife'
  | 'other';

export interface RawCategoryStat {
  category: string;
  attempts: number;
  averageScore: number;
  totalCorrect: number;
  totalQuestions?: number;
  percentageCorrect?: number;
  color?: string;
}

export interface CategoryGroupSummary {
  key: CategoryGroupKey;
  label: string;
  attempts: number;
  averageScore: number;
  totalCorrect: number;
  categories: string[];
  percentageCorrect?: number;
  color?: string;
}

const CATEGORY_GROUP_LABELS: Record<CategoryGroupKey, string> = {
  general: 'General Knowledge',
  science: 'Science & Nature',
  math: 'Mathematics & Logic',
  tech: 'Technology & Engineering',
  history: 'History & Politics',
  economics: 'Economics & Business',
  humanities: 'Humanities & Social',
  geography: 'Geography',
  arts: 'Arts & Culture',
  sports: 'Sports & Games',
  food: 'Food & Cooking',
  entertainment: 'Entertainment & Media',
  mythology: 'Mythology & Folklore',
  medicine: 'Medicine & Health',
  space: 'Space & Astronomy',
  books: 'Books & Authors',
  music: 'Music History & Genres',
  languages: 'World Languages & Linguistics',
  wildlife: 'Wildlife & Zoology',
  other: 'Other Topics'
};

/**
 * Get the display name for a parent group (parent_group from database)
 * Maps database keys like "tech", "arts", "science" to display names
 */
export function getParentTopicDisplayName(parentGroup: string | null | undefined): string | null {
  if (!parentGroup) return null;
  
  // Normalize the parent group key (lowercase, trim)
  const normalizedKey = parentGroup.toLowerCase().trim() as CategoryGroupKey;
  
  // Return the display name if it exists in the mapping
  return CATEGORY_GROUP_LABELS[normalizedKey] || parentGroup;
}

// ---------------------------------------------------------------------------
// Compatibility helper used by unit tests
// ---------------------------------------------------------------------------

export function groupQuestionsByCategory<T extends { category?: any }>(
  questions: T[]
): Record<string, T[]> {
  const grouped: Record<string, T[]> = {};
  for (const q of questions || []) {
    const key = String((q as any).category);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(q);
  }
  return grouped;
}

const DIRECT_CATEGORY_MAP: Record<string, CategoryGroupKey> = {
  'Organic Chemistry': 'science',
  'Biochemistry': 'science',
  'Molecular Biology': 'science',
  'Neuroscience': 'science',
  'Immunology': 'science',
  'Thermodynamics': 'science',
  'Geology': 'science',
  'Astrophysics': 'science',
  'Materials Engineering': 'tech',
  'Machine Learning': 'tech',
  'Computer Architecture': 'tech',
  'Linear Algebra': 'math',
  'Differential Equations': 'math',
  'Probability Theory': 'math',
  'Statistics': 'math',
  'Microeconomics': 'economics',
  'Quantitative Finance': 'economics',
  'European History': 'history',
  'Philosophy': 'humanities',
  // Category reassignments (after reorganization)
  'Mathematics': 'math', // Consolidated from Math
  'General Knowledge': 'general', // Consolidated from General
  // New parent topic mappings
  'Food & Cooking': 'food',
  'Cooking': 'food',
  'Cuisine': 'food',
  'Recipes': 'food',
  'Food History': 'food',
  'Beverages': 'food',
  'Culinary Arts': 'food',
  'Entertainment & Media': 'entertainment',
  'Movies': 'entertainment',
  'TV Shows': 'entertainment',
  'Celebrities': 'entertainment',
  'Pop Culture': 'entertainment',
  'Entertainment Industry': 'entertainment',
  'Television': 'entertainment',
  'Mythology & Folklore': 'mythology',
  'Greek Mythology': 'mythology',
  'Norse Mythology': 'mythology',
  'Folklore': 'mythology',
  'Legends': 'mythology',
  'Myths': 'mythology',
  'Mythological Creatures': 'mythology',
  'Medicine & Health': 'medicine',
  'Medicine': 'medicine',
  'Health': 'medicine',
  'Anatomy': 'medicine',
  'Diseases': 'medicine',
  'Medical History': 'medicine',
  'Healthcare': 'medicine',
  'Space & Astronomy': 'space',
  'Space Exploration': 'space',
  'Planets': 'space',
  'Astronomy': 'space',
  'Space Missions': 'space',
  'Cosmology': 'space',
  'Books & Authors': 'books',
  'Classic Literature': 'books',
  'Modern Literature': 'books',
  'Authors': 'books',
  'Literary Works': 'books',
  'Book History': 'books',
  'Literary Genres': 'books',
  'Music History & Genres': 'music',
  'Music History': 'music',
  'Music Genres': 'music',
  'Musicians': 'music',
  'Music Theory': 'music',
  'Musical Instruments': 'music',
  'Music Industry': 'music',
  'World Languages & Linguistics': 'languages',
  'Languages': 'languages',
  'Linguistics': 'languages',
  'Etymology': 'languages',
  'Language Families': 'languages',
  'Writing Systems': 'languages',
  'Translation': 'languages',
  'Wildlife & Zoology': 'wildlife',
  'Animals': 'wildlife',
  'Wildlife': 'wildlife',
  'Zoology': 'wildlife',
  'Animal Behavior': 'wildlife',
  'Endangered Species': 'wildlife',
  'Marine Life': 'wildlife'
};

const CATEGORY_RULES: { key: CategoryGroupKey; pattern: RegExp }[] = [
  // Order matters - more specific patterns should come first
  { key: 'food', pattern: /(food|cooking|cuisine|recipe|culinary|beverage|gastronomy)/i },
  { key: 'entertainment', pattern: /(entertainment|movie|tv show|celebrity|pop culture|television|cinema)/i },
  { key: 'mythology', pattern: /(mythology|myth|folklore|legend|greek myth|norse myth|mythological)/i },
  { key: 'medicine', pattern: /(medicine|health|anatomy|disease|medical|healthcare|physiology|diagnosis)/i },
  { key: 'space', pattern: /(space|astronomy|planet|cosmos|astrophysics|cosmology|space mission|galaxy)/i },
  { key: 'books', pattern: /(book|author|literature|literary|novel|poetry|writing|publishing)/i },
  { key: 'music', pattern: /(music|musician|genre|song|album|instrument|composer|symphony)/i },
  { key: 'languages', pattern: /(language|linguistics|etymology|translation|grammar|syntax|phonetics)/i },
  { key: 'wildlife', pattern: /(wildlife|animal|zoology|fauna|species|habitat|conservation|endangered)/i },
  { key: 'science', pattern: /(biology|chemistry|physics|science|geology|immunology|neuroscience|ecology|earth|climate)/i },
  { key: 'math', pattern: /(math|algebra|calculus|probability|statistics|geometry|logic|equation)/i },
  { key: 'tech', pattern: /(computer|software|programming|technology|engineering|ai|machine learning|data|architecture|cyber|robotics)/i },
  { key: 'history', pattern: /(history|politic|war|empire|ancient|civilization|revolution)/i },
  { key: 'economics', pattern: /(economics|finance|business|market|accounting|management)/i },
  { key: 'humanities', pattern: /(philosophy|psychology|sociology|anthropology|ethic|culture)/i },
  { key: 'geography', pattern: /(geography|capital|country|continent|map|region)/i },
  { key: 'arts', pattern: /(art|theatre|painting|sculpture|drama|visual art)/i },
  { key: 'sports', pattern: /(sport|athletic|football|soccer|basketball|olympic|tennis|game)/i },
  { key: 'general', pattern: /(general knowledge|trivia|mixed|variety)/i }
];

export const mapCategoryToGroup = (category: string): CategoryGroupKey => {
  if (!category) {
    return 'other';
  }

  const exactMatch = DIRECT_CATEGORY_MAP[category];
  if (exactMatch) {
    return exactMatch;
  }

  const normalized = category.trim();
  for (const rule of CATEGORY_RULES) {
    if (rule.pattern.test(normalized)) {
      return rule.key;
    }
  }

  return 'other';
};

interface MutableCategoryGroup {
  key: CategoryGroupKey;
  label: string;
  attempts: number;
  totalCorrect: number;
  totalQuestions: number;
  weightedScoreSum: number;
  categories: Set<string>;
  colors: string[];
}

export const groupCategoryStats = (stats: RawCategoryStat[]): CategoryGroupSummary[] => {
  const groups = new Map<CategoryGroupKey, MutableCategoryGroup>();

  for (const stat of stats) {
    const key = mapCategoryToGroup(stat.category);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: CATEGORY_GROUP_LABELS[key],
        attempts: 0,
        totalCorrect: 0,
        totalQuestions: 0,
        weightedScoreSum: 0,
        categories: new Set<string>(),
        colors: []
      });
    }

    const group = groups.get(key)!;
    group.attempts += Number(stat.attempts) || 0;
    group.totalCorrect += Number(stat.totalCorrect) || 0;
    group.totalQuestions += Number(stat.totalQuestions) || 0;
    group.weightedScoreSum += Number(stat.averageScore) * Number(stat.attempts);
    if (stat.category) {
      group.categories.add(stat.category);
    }
    if (stat.color && !group.colors.includes(stat.color)) {
      group.colors.push(stat.color);
    }
  }

  const summaries: CategoryGroupSummary[] = Array.from(groups.values())
    .map((group) => {
      const percentageCorrect = group.totalQuestions > 0
        ? Math.round((group.totalCorrect / group.totalQuestions) * 100)
        : undefined;

      // Use the first color from the group, or default gray
      const color = group.colors.length > 0 ? group.colors[0] : '#808080';

      return {
        key: group.key,
        label: group.label,
        attempts: group.attempts,
        totalCorrect: group.totalCorrect,
        averageScore: group.attempts > 0 ? Math.round(group.weightedScoreSum / group.attempts) : 0,
        percentageCorrect,
        color,
        categories: Array.from(group.categories).sort((a, b) => a.localeCompare(b))
      };
    })
    .sort((a, b) => b.attempts - a.attempts);

  return summaries;
};
